import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiCheckCircle, FiDownload } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFinaliseProtocolMutation,
  useGetProtocolDraftQuery,
  useListProtocolVersionsQuery,
  usePatchProtocolNarrativeMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { downloadProtocolVersion } from "./downloadProtocolVersion";
import { ProtocolSectionView } from "./ProtocolSectionView";

export const ProtocolPage = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const apiError = useApiError();
  const displayDate = useDisplayDate();

  const { data: draft, isLoading } = useGetProtocolDraftQuery(eventId ? { eventId } : skipToken);
  const [patch] = usePatchProtocolNarrativeMutation();
  const [finalise, { isLoading: isFinalising }] = useFinaliseProtocolMutation();
  const { data: versionsData, refetch: refetchVersions } = useListProtocolVersionsQuery(
    eventId ? { eventId } : skipToken,
  );

  const versions = versionsData?.versions ?? [];
  const sortedVersions = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);

  const onSaveNarrative = async (editableKey: string, value: string) => {
    if (!eventId) return;
    try {
      await patch({ eventId, protocolDraftPatchDto: { editableKey, value } }).unwrap();
    } catch (e) {
      apiError.trigger(e instanceof Error ? e : new Error("Unknown error"));
    }
  };

  const onFinalise = async () => {
    if (!eventId) return;
    try {
      await finalise({ eventId }).unwrap();
      toast({ title: t("Protocol finalised"), status: "success", position: "top-right" });
      refetchVersions();
    } catch (e) {
      apiError.trigger(e instanceof Error ? e : new Error("Unknown error"));
    }
  };

  return (
    <Box maxW="container.lg" mx="auto">
      <Stack spacing="1" mb="6">
        <Link
          as="button"
          onClick={() => navigate(-1)}
          color="smPrimary"
          fontSize="sm"
          display="inline-flex"
          alignItems="center"
          gap="1"
          alignSelf="flex-start"
        >
          <Icon as={FiArrowLeft} /> {t("Back")}
        </Link>
        <Heading as="h1" size="lg" color="smText">
          {t("Procurement protocol")}
        </Heading>
      </Stack>

      {isLoading ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : (
        <Stack spacing="5">
          {(draft?.sections ?? []).map((section) => (
            <ProtocolSectionView key={section.number} section={section} onSaveNarrative={onSaveNarrative} />
          ))}

          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
            <Flex justify="space-between" align="center" mb="3" gap="3">
              <Heading as="h2" size="sm" color="smText">
                {t("Finalised versions")}
              </Heading>
              <Button
                colorScheme="teal"
                size="sm"
                leftIcon={<Icon as={FiCheckCircle} />}
                onClick={onFinalise}
                isLoading={isFinalising}
              >
                {t("Finalise")}
              </Button>
            </Flex>
            <Divider mb="3" borderColor="smBorder" />
            {sortedVersions.length > 0 ? (
              <Stack spacing="2">
                {sortedVersions.map((version) => (
                  <HStack key={version.versionNumber} justify="space-between">
                    <Text fontSize="sm" color="smText">
                      {t("Version {{number}}", { number: version.versionNumber })}
                      <Text as="span" color="smMuted" ml="2">
                        {displayDate(version.finalisedAt)}
                      </Text>
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="teal"
                      leftIcon={<Icon as={FiDownload} />}
                      onClick={async () => {
                        try {
                          await downloadProtocolVersion({
                            downloadUrl: version.downloadUrl,
                            fileName: `${t("Procurement protocol")} v${version.versionNumber}`,
                          });
                        } catch (e) {
                          apiError.trigger(e instanceof Error ? e : new Error("Unknown error"));
                        }
                      }}
                    >
                      {t("Download")}
                    </Button>
                  </HStack>
                ))}
              </Stack>
            ) : (
              <Text fontSize="sm" color="smMuted">
                {t("No finalised versions yet. Click Finalise to create the first version.")}
              </Text>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
